import './ProductImage.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import { composeRef } from '@boost-sd/core-js';
import type { CTAButtonProps } from '@components/CallToActionButton';
import type { ImageProps } from '@components/Image';
import Image from '@components/Image';
import type { IntegrationProductRatingProps } from '@components/IntegrationProductRating';
import IntegrationProductWishlist from '@components/IntegrationProductWishlist';
import type { ProductLabelProps } from '@components/ProductLabel';
import type { ProductSwatchProps } from '@components/ProductSwatch';
import type { ProductImageGrid } from '@providers/ThemeProvider/types';
import type { ModifiersType } from '@utils';
import {
  createClsNameMap,
  getLoadingStrategyAndFetchPriority,
  getSizes,
  getSrcSet,
  mapModifiers,
  mergeModifiers,
  stripHtml,
} from '@utils';
import { useCallback, useMemo, useRef } from 'react';

export const clsNameMap = createClsNameMap({
  elements: {
    wrapper: createClsNameMap({
      modifiers: [
        'square',
        'aspect-ratio-3-4',
        'portrait',
        'landscape',
        'natural',
        'manual',
        'auto',
        'full-box',
        'crop-top',
        'crop-center',
        'crop-bottom',
        'has-second-image',
        'zoom-in',
      ],
    }),
    img: createClsNameMap({
      modifiers: ['main', 'display-block', 'second', 'as-slider'],
    }),
    row: createClsNameMap({
      modifiers: ['top', 'middle', 'bottom', 'vertical'],
    }),
    column: createClsNameMap({
      modifiers: ['in-top', 'in-middle', 'in-bottom', 'center', 'right', 'left'],
    }),
  },
  modifiers: ['has-border', 'blur'],
})('product-image');

export type ProductImageProps = {
  elements?: {
    saleLabel?: React.ReactElement<ProductLabelProps>;
    soldOutLabel?: React.ReactElement<ProductLabelProps>;
    customLabelByTag?: React.ReactElement<ProductLabelProps>;
    inventoryStatus?: React.ReactElement;
    [swatches: `swatches.${number}`]: React.ReactElement<ProductSwatchProps> | undefined;
    qvBtn?: React.ReactElement<CTAButtonProps>;
    selectOptionBtn?: React.ReactElement<CTAButtonProps>;
    goToDetailBtn?: React.ReactElement<CTAButtonProps>;
    // 3rd App Integration
    rating?: React.ReactElement<IntegrationProductRatingProps>;
  };
  grid?: ProductImageGrid;
  images: string[] | string;
  originalSize?: Array<{
    width: number;
    height: number;
  }>;
  aspectRatio?:
    | 'square'
    | 'aspect-ratio-3-4'
    | 'portrait'
    | 'landscape'
    | 'natural'
    | 'manual'
    | 'auto'
    | 'full-box';
  cropPosition?: 'top' | 'center' | 'bottom';
  altImage?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  isImageSlider?: boolean;
  isAutoRatio?: boolean;
  manualAspectRatio?: `${number}/${number}`;
  autoAspectRatio?: `${number}/${number}`;
  naturalAspectRatio?: `${number}/${number}`;
  hoverEffect?: 'reveal-second-image' | 'zoom-in' | 'reveal-second-image-and-zoom-in';
  imageModifiers?: ModifiersType<typeof clsNameMap, true>;
  productId?: number;
  displayBlock?: boolean;
  /**
   * @default "original"
   */
  loadedImageSizeStrategy?: 'original' | 'fit-container' | 'fit-container-with-dpi';
  isShowWishlist?: boolean;
  /**
   * Define device sizes to generate srcSet
   */
  deviceSizes?: Array<number>;
  /**
   * Define image sizes corresponding to device sizes
   */
  imageSizes?: Array<number>;
};

const ProductImage = (props: ProductImageProps) => {
  const {
    images,
    aspectRatio = 'aspect-ratio-3-4',
    manualAspectRatio,
    autoAspectRatio,
    naturalAspectRatio,
    altImage,
    onClick,
    isImageSlider,
    hoverEffect,
    elements,
    grid,
    cropPosition,
    imageModifiers,
    productId,
    displayBlock,
    isShowWishlist,
    deviceSizes = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1600],
    imageSizes,
  } = props;

  const arrayImage = typeof images === 'string' ? [images] : images;

  const rootElementRef = useRef<HTMLDivElement | null>(null);

  const showSecondImgOnHover =
    hoverEffect === 'reveal-second-image' || hoverEffect === 'reveal-second-image-and-zoom-in';

  const validatedAltImage = useMemo(() => stripHtml(altImage || ''), [altImage]);

  const renderElements = useCallback(() => {
    if (!elements || !grid) return null;

    const renderedElements = [];

    for (const [row, rowData] of Object.entries(grid)) {
      if (!rowData?.elements) continue;

      const rowModifiers = {
        [row]: true,
        vertical: rowData?.direction === 'vertical',
      };

      const rowElement = (
        <div key={row} className={mapModifiers(clsNameMap.row, rowModifiers)}>
          {Object.entries(rowData.elements).map(([column, columnEls]) => {
            const columnModifiers = {
              [`in-${row}`]: true,
              [column]: true,
            };

            const columnElement = (
              <div key={column} className={mapModifiers(clsNameMap.column, columnModifiers)}>
                {columnEls?.map((elementType) => elements[elementType])}
              </div>
            );

            return columnElement;
          })}
        </div>
      );

      renderedElements.push(rowElement);
    }

    return renderedElements;
  }, [elements, grid]);

  const styleRootElement = () => {
    switch (aspectRatio) {
      case 'auto':
        return { aspectRatio: autoAspectRatio };
      case 'natural':
        return { aspectRatio: naturalAspectRatio };
      case 'manual':
        return { aspectRatio: manualAspectRatio };
    }
  };

  const commonImageProps: Pick<ImageProps, 'alt' | 'loading' | 'fetchpriority' | 'sizes'> = {
    ...getLoadingStrategyAndFetchPriority(rootElementRef),
    alt: validatedAltImage,
    sizes: getSizes(rootElementRef, 200),
  };

  return (
    <div
      className={mergeModifiers(clsNameMap.wrapper, [
        {
          'has-second-image': showSecondImgOnHover && !!arrayImage?.[1],
          'zoom-in': hoverEffect === 'zoom-in' || hoverEffect === 'reveal-second-image-and-zoom-in',
          'crop-top': cropPosition === 'top',
          'crop-center': cropPosition === 'center',
          'crop-bottom': cropPosition === 'bottom',
        },
        aspectRatio && [aspectRatio],
      ])}
      style={styleRootElement()}
      onClick={onClick}
      ref={composeRef(rootElementRef)}
    >
      <div className={mapModifiers(clsNameMap, imageModifiers)}>
        {arrayImage?.[0] && (
          <>
            <Image
              id={`product-image-${productId}`}
              className={mapModifiers(clsNameMap.img, {
                main: true,
                'display-block': displayBlock,
                'as-slider': isImageSlider === true,
              })}
              src={arrayImage?.[0]}
              srcSet={getSrcSet(arrayImage?.[0], deviceSizes, imageSizes)}
              {...commonImageProps}
            />
            {showSecondImgOnHover && !!arrayImage?.[1] && (
              <Image
                className={mapModifiers(clsNameMap.img, { second: true })}
                src={arrayImage?.[1]}
                srcSet={getSrcSet(arrayImage?.[1], deviceSizes, imageSizes)}
                {...commonImageProps}
              />
            )}
          </>
        )}
      </div>
      {isShowWishlist && <IntegrationProductWishlist />}
      {renderElements()}
    </div>
  );
};

export default registryComponent('ProductImage', ProductImage);
